"use client";
import { getPlatformOrigin } from "@/configs";

import { CodeStep } from "@/components/login/code-step";
import { EmailStep } from "@/components/login/email-step";
import { type UseLoginOptions, useLogin } from "@/components/login/use-login";
import { useEffect } from "react";

export interface LoginFormProps extends UseLoginOptions {
  showLogo?: boolean;
  className?: string;
  resetToken?: number;
  onCancel?: () => void;
}

export function LoginForm({ onSuccess, showLogo = true, className, resetToken = 0, onCancel }: LoginFormProps) {
  const {
    step,
    email,
    setEmail,
    codeValue,
    isSending,
    isVerifying,
    hasVerificationError,
    canSubmitEmail,
    canResend,
    resendSeconds,
    turnstileSiteKey,
    turnstileResetKey,
    setTurnstileToken,
    errorMsg,
    infoMsg,
    requestLogin,
    verifyCode,
    handleCodeChange,
    goBackToEmail,
    reset,
  } = useLogin({
    onSuccess,
  });

  useEffect(() => {
    reset();
  }, [resetToken, reset]);

  return (
    <div className={`grid grid-cols-1 gap-4 text-left ${className || ""}`}>
      {showLogo ? (
        <>
          <a href={getPlatformOrigin()} target="_blank" rel="noreferrer" className="hover:cursor-pointer w-fit" aria-label="Cantabook Home">
            <img src="/canta.png" alt="Cantabook Logo" width={48} height={48} className="w-12 h-auto dark:invert" />
          </a>
          <p className="mt-2 text-lg font-semibold">Sign in to Cantabook</p>
        </>
      ) : null}

      {step === "email" ? (
        <EmailStep
          email={email}
          setEmail={setEmail}
          isSending={isSending}
          canSubmitEmail={canSubmitEmail}
          errorMsg={errorMsg}
          turnstileSiteKey={turnstileSiteKey}
          turnstileResetKey={turnstileResetKey}
          onTurnstileToken={setTurnstileToken}
          onSubmit={requestLogin}
          onCancel={onCancel}
        />
      ) : (
        <CodeStep
          isSending={isSending}
          canResend={canResend}
          resendSeconds={resendSeconds}
          onResend={requestLogin}
          codeValue={codeValue}
          isVerifying={isVerifying}
          hasError={hasVerificationError}
          errorMsg={errorMsg}
          infoMsg={infoMsg}
          onCodeChange={handleCodeChange}
          onVerify={() => verifyCode()}
          onGoBack={goBackToEmail}
        />
      )}
    </div>
  );
}

export default LoginForm;
